<script setup lang="ts">
  import { SCROLL_ZOOM_MODE } from '@/types/scroll-zoom-mode'
  import leaflet from 'leaflet'
  import { require } from '@logistiek-zonder-papier/lzp-library'
  import { type ModelRef, onBeforeUnmount, onMounted, type Ref, ref } from 'vue'

  const {
    startingCoordinates,
    startingZoom,
    minZoom,
    maxZoom,
    scrollZoomMode,
    tileLayerUrl = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
  } = defineProps<{
    startingCoordinates: [number, number]
    startingZoom: number
    minZoom: number
    maxZoom: number
    scrollZoomMode: SCROLL_ZOOM_MODE
    tileLayerUrl?: string
  }>()

  const mapElement: Ref<HTMLDivElement | undefined> = ref()

  const marker: ModelRef<[number, number] | undefined> = defineModel<[number, number] | undefined>(
    'marker'
  )

  let map: leaflet.Map | undefined
  let leafletMarker: leaflet.Marker | undefined

  function onClick(event: leaflet.LeafletMouseEvent) {
    const { lat, lng } = event.latlng

    marker.value = [lat, lng]

    leafletMarker?.remove()

    leafletMarker = leaflet.marker([lat, lng]).addTo(require(map))
  }

  function onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Shift') {
      map?.scrollWheelZoom.enable()
    }
  }

  function onKeyUp(event: KeyboardEvent) {
    if (event.key === 'Shift') {
      map?.scrollWheelZoom.disable()
    }
  }

  onMounted(() => {
    if (mapElement.value == null) return

    map = leaflet
      .map(mapElement.value, {
        scrollWheelZoom: scrollZoomMode === SCROLL_ZOOM_MODE.ALWAYS,
      })
      .setView(startingCoordinates, startingZoom)

    leaflet.tileLayer(tileLayerUrl, { minZoom: minZoom, maxZoom: maxZoom }).addTo(map)

    map.on('click', onClick)

    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
  })

  onBeforeUnmount(() => {
    if (map) {
      map.remove()
      map = undefined
    }

    window.removeEventListener('keyup', onKeyUp)
    window.removeEventListener('keydown', onKeyDown)
  })
</script>

<template>
  <div class="wrapper">
    <div ref="mapElement" class="map"></div>
    <p v-if="scrollZoomMode === SCROLL_ZOOM_MODE.ON_SHIFT" class="shift-hint">
      (Houd "Shift" ingedrukt om in te zoomen)
    </p>
  </div>
</template>

<style scoped>
  .wrapper {
    background-color: var(--colours-grey-1);
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
  }
  .map {
    width: 100%;
    height: 100%;
  }

  .shift-hint {
    margin: 0;
  }
</style>
